/** biome-ignore-all lint/a11y/noLabelWithoutControl: <explanation> */
import { zodResolver } from "@hookform/resolvers/zod";
import {
	AlertCircle,
	BarChart3,
	Calendar,
	ChevronDown,
	Copy,
	Eye,
	FileText,
	Filter,
	ImagePlus,
	ListChecks,
	Loader2,
	Image as LucideImage,
	MoreVertical,
	Plus,
	Save,
	Settings2,
	Star,
	Trash2,
	Trophy,
	X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useDebounce } from "usehooks-ts";
import { z } from "zod";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button, buttonVariants } from "@/components/ui/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAnalyticsEventNames } from "@/hooks/useAnalyticsEventNames";
import {
	type RewardsGrantUser,
	usePointGrants,
	useRewardsUsers,
} from "@/hooks/usePointGrants";
import {
	createEmptyQuestForm,
	formatDisplayDate,
	type QuestFormData,
	type QuestStatus,
	questDetailsToFormData,
	questFormSchema,
	type RewardsQuestDetails,
	useCreateQuest,
	useDeleteQuest,
	useQuestImageUpload,
	useRewardsQuests,
	useUpdateQuest,
} from "@/hooks/useRewardsQuests";
import { cn } from "@/lib/utils";
import IsConnectedWrapper from "../components/IsConnectedWrapper";

// --- Constants ---

const statusConfig: Record<
	QuestStatus,
	{ label: string; className: string; dot: string }
> = {
	draft: {
		label: "Draft",
		className: "bg-yellow-400/10 text-yellow-400 border-yellow-400/20",
		dot: "bg-yellow-400",
	},
	active: {
		label: "Active",
		className: "bg-success/10 text-success border-success/20",
		dot: "bg-success",
	},
	archived: {
		label: "Archived",
		className: "bg-text-tertiary/10 text-text-tertiary border-text-tertiary/20",
		dot: "bg-text-tertiary",
	},
};

export const inputClassName =
	"w-full bg-bg-secondary border border-divider rounded-lg px-3 py-2.5 text-text-default placeholder-text-placeholder text-sm focus:outline-none focus:ring-1 focus:ring-brand/50 focus:border-brand/50 transition-all";

const labelClassName = "text-sm font-medium text-text-secondary mb-1.5 block";

// Event filter configuration per event type
type EventFilterField = {
	key: string;
	label: string;
	type: "text" | "checkbox";
	placeholder?: string;
};

const EVENT_FILTER_CONFIG: Record<string, EventFilterField[]> = {
	new_chat_with_model: [
		{
			key: "modelId",
			label: "Model ID",
			type: "text",
			placeholder: "e.g., gpt-4o, claude-3-opus",
		},
	],
	new_chat_with_agent: [
		{
			key: "agentName",
			label: "Agent Name",
			type: "text",
			placeholder: "e.g., GitHub Explorer",
		},
		{
			key: "modelId",
			label: "Model ID",
			type: "text",
			placeholder: "e.g., gpt-4o, claude-3-opus",
		},
	],
	continued_chat_with_agent: [
		{
			key: "agentName",
			label: "Agent Name",
			type: "text",
			placeholder: "e.g., GitHub Explorer",
		},
		{
			key: "modelId",
			label: "Model ID",
			type: "text",
			placeholder: "e.g., gpt-4o, claude-3-opus",
		},
	],
	new_chat_with_own_agent: [
		{
			key: "agentName",
			label: "Agent Name",
			type: "text",
			placeholder: "e.g., My Custom Agent",
		},
		{
			key: "modelId",
			label: "Model ID",
			type: "text",
			placeholder: "e.g., gpt-4o, claude-3-opus",
		},
	],
	agent_created: [
		{
			key: "agentName",
			label: "Agent Name",
			type: "text",
			placeholder: "e.g., GitHub Explorer",
		},
		{
			key: "modelUsed",
			label: "Model Used",
			type: "text",
			placeholder: "e.g., gpt-4o",
		},
		{
			key: "toolCount",
			label: "Min Tool Count",
			type: "text",
			placeholder: "e.g., 3",
		},
	],
	credits_purchased: [
		{
			key: "creditsAmount",
			label: "Min Credits Amount",
			type: "text",
			placeholder: "e.g., 100",
		},
		{
			key: "chainId",
			label: "Chain ID",
			type: "text",
			placeholder: "e.g., 1, 8453",
		},
	],
	x402_payment_completed: [
		{
			key: "toolName",
			label: "Tool Name",
			type: "text",
			placeholder: "e.g., web-search",
		},
		{
			key: "network",
			label: "Network",
			type: "text",
			placeholder: "e.g., base, ethereum",
		},
	],
};

const POINT_GRANT_SOURCE_OPTIONS = [
	{ value: "trivia", label: "Trivia" },
	{ value: "other", label: "Other" },
] as const;

const pointGrantFormSchema = z.object({
	userId: z.string().uuid("User ID must be a valid UUID"),
	points: z.number().int().positive("Points must be greater than 0"),
	source: z.enum(["trivia", "other"], {
		message: "Source is required",
	}),
	sourceRef: z.string().trim().min(1, "Source reference is required"),
	description: z.string().optional(),
	metadataJson: z.string().optional(),
});

type PointGrantFormData = z.infer<typeof pointGrantFormSchema>;

const createPointGrantDefaults = (): PointGrantFormData => ({
	userId: "",
	points: 50,
	source: "trivia",
	sourceRef: "",
	description: "",
	metadataJson: "",
});

// --- Main Component ---

export default function RewardsAdminPage() {
	const [searchParams, setSearchParams] = useSearchParams();
	const { data: analyticsEventNames = [] } = useAnalyticsEventNames();
	const {
		data: quests = [],
		isLoading,
		error: queryError,
	} = useRewardsQuests();

	const [sheetOpen, setSheetOpen] = useState(false);
	const [editingQuest, setEditingQuest] = useState<RewardsQuestDetails | null>(
		null,
	);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [pointGrantDialogOpen, setPointGrantDialogOpen] = useState(false);
	const [questToDelete, setQuestToDelete] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [metadataOpen, setMetadataOpen] = useState(false);

	const createMutation = useCreateQuest();
	const updateMutation = useUpdateQuest();
	const deleteMutation = useDeleteQuest();
	const imageUploadMutation = useQuestImageUpload();
	const pointGrantsMutation = usePointGrants();

	const fileInputRef = useRef<HTMLInputElement>(null);

	const form = useForm<QuestFormData>({
		resolver: zodResolver(questFormSchema),
		defaultValues: createEmptyQuestForm(),
	});
	const pointGrantForm = useForm<PointGrantFormData>({
		resolver: zodResolver(pointGrantFormSchema),
		defaultValues: createPointGrantDefaults(),
	});
	const [pointGrantUserSearch, setPointGrantUserSearch] = useState("");
	const [selectedRewardsUser, setSelectedRewardsUser] =
		useState<RewardsGrantUser | null>(null);
	const debouncedPointGrantUserSearch = useDebounce(pointGrantUserSearch, 300);

	const imageUrl = form.watch("imageUrl");
	const isGrantSubmitting = pointGrantsMutation.isPending;
	const sourceOptions = POINT_GRANT_SOURCE_OPTIONS;

	const {
		data: rewardsUsersData,
		isLoading: rewardsUsersLoading,
		error: rewardsUsersError,
	} = useRewardsUsers({
		query: debouncedPointGrantUserSearch,
		limit: 8,
		enabled: pointGrantDialogOpen,
	});
	const rewardsUsers = rewardsUsersData?.users ?? [];

	const formatRewardsUserLabel = useCallback((user: RewardsGrantUser) => {
		const display =
			user.displayName ||
			user.email ||
			user.selectedShinkaiIdentity ||
			user.walletAddress ||
			user.id;
		const secondary =
			user.email ||
			user.selectedShinkaiIdentity ||
			user.walletAddress ||
			user.discordUsername;
		return secondary && secondary !== display
			? `${display} · ${secondary}`
			: display;
	}, []);

	const handleSelectRewardsUser = useCallback(
		(user: RewardsGrantUser) => {
			pointGrantForm.setValue("userId", user.id, {
				shouldDirty: true,
				shouldValidate: true,
			});
			setPointGrantUserSearch(formatRewardsUserLabel(user));
			setSelectedRewardsUser(user);
			pointGrantForm.clearErrors("userId");

			const currentSourceRef = pointGrantForm.getValues("sourceRef").trim();
			if (!currentSourceRef) {
				const source = pointGrantForm.getValues("source") || "other";
				const date = new Date().toISOString().slice(0, 10);
				pointGrantForm.setValue(
					"sourceRef",
					`${source}-${date}-${user.id.slice(0, 8)}`,
					{
						shouldDirty: true,
					},
				);
			}
		},
		[formatRewardsUserLabel, pointGrantForm],
	);

	const handleImageSelect = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (!file) return;

			// Reset file input so the same file can be re-selected
			e.target.value = "";

			const result = await imageUploadMutation
				.mutateAsync({
					file,
					questId: editingQuest?.quest.id,
				})
				.catch(() => null);
			if (result) {
				form.setValue("imageUrl", result.publicUrl, { shouldDirty: true });
				form.clearErrors("imageUrl");
			}
		},
		[imageUploadMutation, editingQuest, form],
	);

	const handleImageRemove = useCallback(() => {
		form.setValue("imageUrl", null, { shouldDirty: true });
		imageUploadMutation.reset();
	}, [form, imageUploadMutation]);

	const { fields, append, remove } = useFieldArray({
		control: form.control,
		name: "steps",
	});

	const isCreating = !editingQuest;
	const isImageUploading = imageUploadMutation.isPending;
	const isSaving = createMutation.isPending || updateMutation.isPending;
	const mutationError = createMutation.error || updateMutation.error;

	const openCreateSheet = () => {
		setEditingQuest(null);
		form.reset(createEmptyQuestForm());
		setSheetOpen(true);
	};

	const openEditSheet = (quest: RewardsQuestDetails) => {
		setEditingQuest(quest);
		form.reset(questDetailsToFormData(quest));
		setSheetOpen(true);
	};

	const closeSheet = () => {
		setSheetOpen(false);
		setEditingQuest(null);
		form.reset(createEmptyQuestForm());
		createMutation.reset();
		updateMutation.reset();
		imageUploadMutation.reset();
	};

	const openPointGrantDialog = useCallback(() => {
		pointGrantForm.reset(createPointGrantDefaults());
		setPointGrantUserSearch("");
		setSelectedRewardsUser(null);
		setMetadataOpen(false);
		pointGrantsMutation.reset();
		setPointGrantDialogOpen(true);
	}, [pointGrantForm, pointGrantsMutation]);

	const closePointGrantDialog = useCallback(() => {
		setPointGrantDialogOpen(false);
		pointGrantForm.reset(createPointGrantDefaults());
		setPointGrantUserSearch("");
		setSelectedRewardsUser(null);
		setMetadataOpen(false);
		pointGrantsMutation.reset();
	}, [pointGrantForm, pointGrantsMutation]);

	useEffect(() => {
		if (searchParams.get("open") !== "grant") return;
		openPointGrantDialog();
		const nextParams = new URLSearchParams(searchParams);
		nextParams.delete("open");
		setSearchParams(nextParams, { replace: true });
	}, [searchParams, setSearchParams, openPointGrantDialog]);

	const onSubmit = async (data: QuestFormData) => {
		// Require cover image when creating a new quest
		if (!editingQuest && !data.imageUrl) {
			form.setError("imageUrl", {
				type: "manual",
				message: "Cover image is required",
			});
			toast.error("Cover image is required to create a challenge.");
			return;
		}
		if (editingQuest) {
			await updateMutation.mutateAsync({ id: editingQuest.quest.id, data });
		} else {
			await createMutation.mutateAsync(data);
		}
		closeSheet();
	};

	const onPointGrantSubmit = async (data: PointGrantFormData) => {
		const selectedSourceOption = sourceOptions.find(
			(sourceOption) => sourceOption.value === data.source.trim(),
		);
		if (!selectedSourceOption) {
			pointGrantForm.setError("source", {
				type: "manual",
				message: "Select a valid source",
			});
			return;
		}

		let metadata: Record<string, unknown> | null = null;
		const rawMetadata = data.metadataJson?.trim();

		if (rawMetadata) {
			try {
				const parsed = JSON.parse(rawMetadata) as unknown;
				if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
					pointGrantForm.setError("metadataJson", {
						type: "manual",
						message: "Metadata must be a valid JSON object",
					});
					return;
				}
				metadata = parsed as Record<string, unknown>;
			} catch {
				pointGrantForm.setError("metadataJson", {
					type: "manual",
					message: "Metadata must be valid JSON",
				});
				return;
			}
		}

		try {
			await pointGrantsMutation.mutateAsync({
				grants: [
					{
						userId: data.userId,
						points: data.points,
						source: selectedSourceOption.value,
						sourceRef: data.sourceRef.trim(),
						description: data.description?.trim() || null,
						metadata,
					},
				],
			});

			toast.success("Manual points granted");
			closePointGrantDialog();
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to grant points",
			);
		}
	};

	const openDeleteDialog = (questId: string, questName: string) => {
		setQuestToDelete({ id: questId, name: questName });
		setDeleteDialogOpen(true);
	};

	const confirmDelete = () => {
		if (questToDelete) {
			deleteMutation.mutate(questToDelete.id);
		}
		setDeleteDialogOpen(false);
		setQuestToDelete(null);
	};

	const handleDuplicate = (quest: RewardsQuestDetails) => {
		const formData = questDetailsToFormData(quest);
		// Remove id to create a new quest, and update the name
		form.reset({
			...formData,
			id: null,
			name: `${formData.name} (Copy)`,
			status: "draft",
			steps: formData.steps.map((step) => ({
				...step,
				id: undefined, // Remove step ids to create new steps
				eventPropsFilter: step.eventPropsFilter ?? null, // Preserve filters
			})),
		});
		setEditingQuest(null);
		setSheetOpen(true);
	};

	const addStep = () => {
		append({
			name: "",
			description: "",
			eventName: analyticsEventNames[0] ?? "",
			requiredCount: 1,
			withinSeconds: null,
			eventPropsFilter: {},
		});
	};

	// Group quests by status
	const groupedQuests = useMemo(() => {
		const active = quests.filter((q) => q.quest.status === "active");
		const draft = quests.filter((q) => q.quest.status === "draft");
		const archived = quests.filter((q) => q.quest.status === "archived");
		return { active, draft, archived };
	}, [quests]);

	const displayError =
		queryError?.message || deleteMutation.error?.message || null;

	return (
		<main className="grow pb-20 px-6 font-inter min-h-[calc(100vh-500px)]">
			<div className="max-w-5xl mx-auto space-y-8">
				<IsConnectedWrapper>
					{/* Header */}
					<section className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
						<div className="space-y-1">
							<h1 className="font-clash text-4xl font-medium text-text-default tracking-tight">
								Challenge Manager
							</h1>
							<p className="text-base text-text-secondary">
								Create and manage challenges for users to complete
							</p>
						</div>
						<div className="flex items-center gap-2 shrink-0">
							<Button onClick={openCreateSheet} className="gap-2">
								<Plus className="w-4 h-4" />
								Create Challenge
							</Button>
						</div>
					</section>
					{/* Error Alert */}
					{displayError && !sheetOpen && (
						<div className="flex items-center gap-3 p-4 rounded-xl border border-destructive/30 bg-destructive/10">
							<AlertCircle className="w-5 h-5 text-destructive shrink-0" />
							<p className="text-sm text-destructive">{displayError}</p>
						</div>
					)}
					{/* Loading State */}
					{isLoading && (
						<div className="flex items-center justify-center py-20">
							<Loader2 className="w-8 h-8 animate-spin text-brand" />
						</div>
					)}
					{/* Empty State */}
					{!isLoading && quests.length === 0 && (
						<div className="text-center py-20 space-y-4">
							<div className="w-16 h-16 rounded-2xl bg-bg-tertiary/50 mx-auto flex items-center justify-center">
								<Trophy className="w-7 h-7 text-text-tertiary" />
							</div>
							<div>
								<p className="text-text-default font-medium">
									No challenges yet
								</p>
								<p className="text-text-secondary text-sm mt-1">
									Create your first challenge to get started
								</p>
							</div>
							<Button onClick={openCreateSheet} className="gap-2 mt-4">
								<Plus className="w-4 h-4" />
								Create Challenge
							</Button>
						</div>
					)}
					{/* Quest List */}
					{!isLoading && quests.length > 0 && (
						<div className="space-y-8">
							{groupedQuests.active.length > 0 && (
								<QuestGroup
									title="Active"
									quests={groupedQuests.active}
									onEdit={openEditSheet}
									onDelete={openDeleteDialog}
									onDuplicate={handleDuplicate}
								/>
							)}
							{groupedQuests.draft.length > 0 && (
								<QuestGroup
									title="Drafts"
									quests={groupedQuests.draft}
									onEdit={openEditSheet}
									onDelete={openDeleteDialog}
									onDuplicate={handleDuplicate}
								/>
							)}
							{groupedQuests.archived.length > 0 && (
								<QuestGroup
									title="Archived"
									quests={groupedQuests.archived}
									onEdit={openEditSheet}
									onDelete={openDeleteDialog}
									onDuplicate={handleDuplicate}
								/>
							)}
						</div>
					)}
					{/* Create/Edit Dialog */}
					<Dialog open={sheetOpen} onOpenChange={setSheetOpen}>
						<DialogContent className="max-w-4xl p-0 gap-0 max-h-[90vh] overflow-hidden">
							<form
								onSubmit={form.handleSubmit(onSubmit)}
								className="flex flex-col max-h-[90vh]"
							>
								{/* Header */}
								<DialogHeader className="px-6 pt-6 pb-4 border-b border-divider shrink-0">
									<DialogTitle>
										{isCreating ? "Create Challenge" : "Edit Challenge"}
									</DialogTitle>
									<DialogDescription>
										{isCreating
											? "Set up a new challenge for users to complete"
											: `Editing "${editingQuest?.quest.name}"`}
									</DialogDescription>
								</DialogHeader>

								{/* Two-column body */}
								<div className="flex flex-1 overflow-hidden">
									{/* Left: Form Fields */}
									<div className="flex-1 overflow-y-auto px-6 py-6 space-y-1">
										{/* Error */}
										{mutationError && (
											<div className="flex items-center gap-3 p-3 rounded-lg border border-destructive/30 bg-destructive/10">
												<AlertCircle className="w-4 h-4 text-destructive shrink-0" />
												<p className="text-sm text-destructive">
													{mutationError.message}
												</p>
											</div>
										)}

										{/* ──── Section: Details ──── */}
										<div className="space-y-4 pt-4">
											<SectionHeader icon={FileText} title="Details" />

											<label className="block">
												<span className={labelClassName}>Challenge Name *</span>
												<input
													type="text"
													{...form.register("name")}
													placeholder="e.g., First Agent Created"
													className={cn(
														inputClassName,
														form.formState.errors.name &&
															"border-destructive focus:border-destructive focus:ring-destructive/50",
													)}
												/>
												{form.formState.errors.name && (
													<p className="text-xs text-destructive mt-1">
														{form.formState.errors.name.message}
													</p>
												)}
											</label>

											<label className="block">
												<span className={labelClassName}>Description</span>
												<textarea
													{...form.register("description")}
													placeholder="Describe what users need to do..."
													rows={3}
													className={inputClassName}
												/>
												<p className="text-[10px] text-text-tertiary mt-1">
													Shown on the card below the image. Keep it concise.
												</p>
											</label>

											{/* Cover Image */}
											<div className="space-y-1.5">
												<span className={labelClassName}>
													Cover Image{" "}
													{isCreating && (
														<span className="text-destructive">*</span>
													)}
												</span>
												<input
													ref={fileInputRef}
													type="file"
													accept="image/webp,image/png,image/jpeg,image/gif"
													className="hidden"
													onChange={handleImageSelect}
												/>
												{imageUrl ? (
													<div className="max-w-sm relative group/img rounded-xl overflow-hidden border border-divider bg-bg-tertiary/30">
														<img
															src={imageUrl}
															alt="Quest cover"
															className="w-full aspect-[4/3] object-cover"
														/>
														<div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center gap-2">
															<Button
																type="button"
																variant="tertiary"
																size="xs"
																onClick={() => fileInputRef.current?.click()}
																className="bg-bg-secondary/80 hover:bg-bg-secondary"
																disabled={imageUploadMutation.isPending}
															>
																<ImagePlus className="w-3.5 h-3.5 mr-1.5" />
																Replace
															</Button>
															<Button
																type="button"
																variant="tertiary"
																size="xs"
																onClick={handleImageRemove}
																className="bg-bg-secondary/80 hover:bg-destructive/80 hover:text-white"
															>
																<X className="w-3.5 h-3.5 mr-1.5" />
																Remove
															</Button>
														</div>
													</div>
												) : (
													<button
														type="button"
														onClick={() => fileInputRef.current?.click()}
														disabled={imageUploadMutation.isPending}
														className={cn(
															"w-full flex flex-col items-center justify-center gap-2 py-8 rounded-xl border border-dashed border-divider bg-bg-tertiary/20 hover:bg-bg-tertiary/40 hover:border-brand/30 transition-all cursor-pointer",
															imageUploadMutation.isPending &&
																"opacity-60 cursor-not-allowed",
															form.formState.errors.imageUrl &&
																"border-destructive/60 bg-destructive/5",
														)}
													>
														{imageUploadMutation.isPending ? (
															<Loader2 className="w-6 h-6 animate-spin text-text-tertiary" />
														) : (
															<ImagePlus className="w-6 h-6 text-text-tertiary" />
														)}
														<span className="text-xs text-text-tertiary">
															{imageUploadMutation.isPending
																? "Uploading..."
																: "Click to upload cover image"}
														</span>
													</button>
												)}
												{imageUploadMutation.error && (
													<p className="text-xs text-destructive mt-1">
														{imageUploadMutation.error.message}
													</p>
												)}
												{form.formState.errors.imageUrl && (
													<p className="text-xs text-destructive mt-1">
														{form.formState.errors.imageUrl.message}
													</p>
												)}
											</div>
										</div>

										{/* ──── Section: Settings ──── */}
										<div className="space-y-4 pt-6">
											<SectionHeader icon={Settings2} title="Settings" />

											{/* Status as pill buttons */}
											<div>
												<span className={labelClassName}>Status</span>
												<Controller
													control={form.control}
													name="status"
													render={({ field }) => (
														<div className="flex items-center gap-2 mt-1">
															{(["draft", "active", "archived"] as const).map(
																(s) => {
																	const cfg = statusConfig[s];
																	const isSelected = field.value === s;
																	return (
																		<button
																			key={s}
																			type="button"
																			onClick={() => field.onChange(s)}
																			className={cn(
																				"flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium border transition-all",
																				isSelected
																					? cfg.className
																					: "border-divider text-text-tertiary hover:text-text-secondary hover:border-white/10 bg-transparent",
																			)}
																		>
																			<div
																				className={cn(
																					"w-1.5 h-1.5 rounded-full",
																					isSelected
																						? cfg.dot
																						: "bg-text-tertiary",
																				)}
																			/>
																			{cfg.label}
																		</button>
																	);
																},
															)}
														</div>
													)}
												/>
											</div>

											<div className="grid grid-cols-2 gap-4">
												<label className="block">
													<span className={labelClassName}>Points *</span>
													<input
														type="number"
														{...form.register("points", {
															valueAsNumber: true,
														})}
														min={0}
														className={cn(
															inputClassName,
															form.formState.errors.points &&
																"border-destructive focus:border-destructive focus:ring-destructive/50",
														)}
													/>
													{form.formState.errors.points && (
														<p className="text-xs text-destructive mt-1">
															{form.formState.errors.points.message}
														</p>
													)}
												</label>

												<label className="block">
													<span className={labelClassName}>
														Max Completions / User
													</span>
													<input
														type="number"
														{...form.register("maxCompletionsPerUser", {
															valueAsNumber: true,
														})}
														placeholder="∞ Unlimited"
														className={inputClassName}
													/>
													<p className="text-[10px] text-text-tertiary mt-1">
														Leave empty for unlimited
													</p>
												</label>
											</div>
										</div>

										{/* ──── Section: Schedule ──── */}
										<div className="space-y-4 pt-6">
											<SectionHeader
												icon={Calendar}
												title="Schedule"
												subtitle="Optional — leave empty for always-on challenges"
											/>
											<div className="grid grid-cols-2 gap-4">
												<label className="block">
													<span className={labelClassName}>Start Date</span>
													<input
														type="datetime-local"
														{...form.register("startAt")}
														className={inputClassName}
													/>
												</label>

												<label className="block">
													<span className={labelClassName}>End Date</span>
													<input
														type="datetime-local"
														{...form.register("endAt")}
														className={inputClassName}
													/>
												</label>
											</div>
										</div>

										{/* ──── Section: Steps ──── */}
										<div className="space-y-3 pt-6">
											<div className="flex items-center justify-between">
												<SectionHeader
													icon={ListChecks}
													title={`Steps (${fields.length})`}
													subtitle="Each step maps to an analytics event"
												/>
												<Button
													type="button"
													variant="tertiary"
													size="xs"
													onClick={addStep}
													className="gap-1.5 shrink-0"
												>
													<Plus className="w-3.5 h-3.5" />
													Add Step
												</Button>
											</div>

											{fields.length === 0 ? (
												<button
													type="button"
													onClick={addStep}
													className="w-full flex flex-col items-center justify-center gap-2 py-10 border border-dashed border-divider rounded-xl hover:border-brand/30 hover:bg-bg-tertiary/20 transition-all cursor-pointer"
												>
													<div className="w-10 h-10 rounded-xl bg-bg-tertiary/50 flex items-center justify-center">
														<Plus className="w-5 h-5 text-text-tertiary" />
													</div>
													<div className="text-center">
														<p className="text-sm text-text-secondary font-medium">
															Add your first step
														</p>
														<p className="text-[10px] text-text-tertiary mt-0.5">
															Steps define what users need to do to complete
															this challenge
														</p>
													</div>
												</button>
											) : (
												<div className="space-y-3">
													{fields.map((field, index) => (
														<div
															key={field.id}
															className="rounded-xl border border-divider bg-bg-tertiary/20 overflow-hidden"
														>
															{/* Step Header — always visible */}
															<div className="flex items-center gap-3 px-4 py-3 border-b border-divider/50">
																<div className="w-6 h-6 rounded-full bg-brand/10 border border-brand/20 flex items-center justify-center shrink-0">
																	<span className="text-[10px] font-bold text-brand">
																		{index + 1}
																	</span>
																</div>
																<div className="flex-1 min-w-0">
																	<input
																		type="text"
																		{...form.register(`steps.${index}.name`)}
																		placeholder="Step name *"
																		className={cn(
																			"w-full bg-transparent text-sm text-text-default placeholder-text-placeholder focus:outline-none",
																			form.formState.errors.steps?.[index]
																				?.name &&
																				"text-destructive placeholder-destructive/50",
																		)}
																	/>
																</div>
																<Button
																	type="button"
																	variant="tertiary"
																	size="icon"
																	onClick={() => remove(index)}
																	className="w-7 h-7 text-text-tertiary hover:text-destructive hover:bg-destructive/10 shrink-0"
																>
																	<Trash2 className="w-3.5 h-3.5" />
																</Button>
															</div>

															{/* Step Body — compact 2-col layout */}
															<div className="px-4 py-3 space-y-3">
																<div className="grid grid-cols-2 gap-3">
																	<div className="block">
																		<span className="text-[10px] font-medium text-text-tertiary uppercase tracking-wider mb-1 block">
																			Event
																		</span>
																		<Controller
																			control={form.control}
																			name={`steps.${index}.eventName`}
																			render={({ field: selectField }) => (
																				<Select
																					value={selectField.value}
																					onValueChange={selectField.onChange}
																				>
																					<SelectTrigger
																						className={cn(
																							inputClassName,
																							"text-xs py-2",
																							form.formState.errors.steps?.[
																								index
																							]?.eventName &&
																								"border-destructive",
																						)}
																					>
																						<SelectValue placeholder="Select event" />
																					</SelectTrigger>
																					<SelectContent>
																						{analyticsEventNames?.map(
																							(eventName) => (
																								<SelectItem
																									key={eventName}
																									value={eventName}
																								>
																									{eventName}
																								</SelectItem>
																							),
																						)}
																					</SelectContent>
																				</Select>
																			)}
																		/>
																	</div>
																	<div className="grid grid-cols-2 gap-2">
																		<label className="block">
																			<span className="text-[10px] font-medium text-text-tertiary uppercase tracking-wider mb-1 block">
																				Count
																			</span>
																			<input
																				type="number"
																				{...form.register(
																					`steps.${index}.requiredCount`,
																					{ valueAsNumber: true },
																				)}
																				min={1}
																				className={cn(
																					inputClassName,
																					"text-xs py-2",
																				)}
																			/>
																		</label>
																		<label className="block">
																			<span className="text-[10px] font-medium text-text-tertiary uppercase tracking-wider mb-1 block">
																				Time (s)
																			</span>
																			<input
																				type="number"
																				{...form.register(
																					`steps.${index}.withinSeconds`,
																					{ valueAsNumber: true },
																				)}
																				placeholder="∞"
																				min={1}
																				className={cn(
																					inputClassName,
																					"text-xs py-2",
																				)}
																			/>
																		</label>
																	</div>
																</div>

																<label className="block">
																	<span className="text-[10px] font-medium text-text-tertiary uppercase tracking-wider mb-1 block">
																		Description
																	</span>
																	<input
																		type="text"
																		{...form.register(
																			`steps.${index}.description`,
																		)}
																		placeholder="Optional — shown to users on the card"
																		className={cn(
																			inputClassName,
																			"text-xs py-2",
																		)}
																	/>
																</label>

																{/* Event Filters */}
																<EventFiltersSection
																	eventName={form.watch(
																		`steps.${index}.eventName`,
																	)}
																	index={index}
																	form={form}
																/>
															</div>
														</div>
													))}
												</div>
											)}
										</div>
									</div>

									{/* Right: Live Preview (sticky) */}
									<div className="hidden md:flex w-[280px] shrink-0 border-l border-divider bg-[#0e0e10] flex-col">
										<div className="px-4 py-3 border-b border-white/5">
											<div className="flex items-center gap-2">
												<Eye className="w-3.5 h-3.5 text-text-tertiary" />
												<span className="text-xs font-medium text-text-secondary">
													Live Preview
												</span>
											</div>
										</div>
										<div className="flex-1 flex items-start justify-center p-4 overflow-y-auto">
											<LivePreviewCard
												name={form.watch("name")}
												description={form.watch("description") ?? null}
												imageUrl={form.watch("imageUrl") ?? null}
												points={form.watch("points")}
												steps={form.watch("steps")}
											/>
										</div>
									</div>
								</div>

								{/* Footer Actions */}
								<div className="px-6 py-4 border-t border-divider bg-bg-tertiary/30 flex items-center justify-between gap-3 shrink-0">
									{editingQuest && (
										<Button
											type="button"
											variant="outline"
											onClick={() =>
												openDeleteDialog(
													editingQuest.quest.id,
													editingQuest.quest.name,
												)
											}
											disabled={
												isSaving || isImageUploading || deleteMutation.isPending
											}
											className="gap-2 border-divider hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive"
										>
											<Trash2 className="w-4 h-4" />
											Delete
										</Button>
									)}
									<div
										className={cn(
											"flex items-center gap-3",
											!editingQuest && "ml-auto",
										)}
									>
										<Button
											type="button"
											variant="outline"
											onClick={closeSheet}
											className="border-divider"
										>
											Cancel
										</Button>
										<Button
											type="submit"
											disabled={isSaving || isImageUploading}
											className="gap-2"
										>
											{isSaving || isImageUploading ? (
												<Loader2 className="w-4 h-4 animate-spin" />
											) : (
												<Save className="w-4 h-4" />
											)}
											{isImageUploading
												? "Uploading Image..."
												: isSaving
													? "Saving..."
													: "Save Challenge"}
										</Button>
									</div>
								</div>
							</form>
						</DialogContent>
					</Dialog>
					{/* Manual Points Grant Dialog */}
					<Dialog
						open={pointGrantDialogOpen}
						onOpenChange={(open) => {
							if (open) {
								setPointGrantDialogOpen(true);
								return;
							}
							closePointGrantDialog();
						}}
					>
						<DialogContent className="max-w-xl">
							<form
								onSubmit={pointGrantForm.handleSubmit(onPointGrantSubmit)}
								className="space-y-4"
							>
								<DialogHeader>
									<DialogTitle>Grant Manual Points</DialogTitle>
									<DialogDescription>
										Award points for one user at a time. For example trivia
										winners or one-off adjustments.
									</DialogDescription>
								</DialogHeader>

								<label className="block">
									<span className={labelClassName}>Find User</span>
									<Input
										type="text"
										value={pointGrantUserSearch}
										onChange={(event) => {
											setPointGrantUserSearch(event.target.value);
										}}
										placeholder="Search by email, identity, wallet, Discord..."
									/>
									<p className="text-[10px] text-text-tertiary mt-1">
										Type at least 2 characters, then select a user to fill User
										ID automatically.
									</p>

									{pointGrantUserSearch.trim().length >= 2 && (
										<div className="mt-2 rounded-lg border border-divider bg-bg-tertiary/20 max-h-40 overflow-y-auto">
											{rewardsUsersLoading ? (
												<div className="flex items-center gap-2 px-3 py-2 text-xs text-text-tertiary">
													<Loader2 className="w-3.5 h-3.5 animate-spin" />
													Searching users...
												</div>
											) : rewardsUsersError instanceof Error ? (
												<div className="px-3 py-2 text-xs text-destructive">
													{rewardsUsersError.message}
												</div>
											) : rewardsUsers.length === 0 ? (
												<div className="px-3 py-2 text-xs text-text-tertiary">
													No users found.
												</div>
											) : (
												rewardsUsers.map((user) => (
													<button
														key={user.id}
														type="button"
														onClick={() => handleSelectRewardsUser(user)}
														className="w-full text-left px-3 py-2 border-b border-divider/40 last:border-b-0 hover:bg-bg-tertiary/60 transition-colors"
													>
														<p className="text-xs text-text-default">
															{formatRewardsUserLabel(user)}
														</p>
														<p className="text-[10px] text-text-tertiary">
															{user.id}
														</p>
													</button>
												))
											)}
										</div>
									)}
								</label>

								{selectedRewardsUser && (
									<div className="rounded-lg border border-divider bg-bg-tertiary/20 px-3 py-2">
										<p className="text-[10px] text-text-tertiary uppercase tracking-wider mb-1">
											Selected User
										</p>
										<p className="text-xs text-text-default">
											{formatRewardsUserLabel(selectedRewardsUser)}
										</p>
										<p className="text-[10px] text-text-tertiary">
											{selectedRewardsUser.id}
										</p>
									</div>
								)}

								<div className="grid grid-cols-2 gap-3">
									<label className="block col-span-2">
										<span className={labelClassName}>User ID (UUID) *</span>
										<Controller
											control={pointGrantForm.control}
											name="userId"
											render={({ field }) => (
												<Input
													type="text"
													value={field.value}
													onChange={(event) => {
														field.onChange(event.target.value);
														if (
															selectedRewardsUser &&
															event.target.value !== selectedRewardsUser.id
														) {
															setSelectedRewardsUser(null);
														}
													}}
													placeholder="e.g., 68325bf3-f827-47e5-9aab-78dba846d048"
													className={cn(
														pointGrantForm.formState.errors.userId &&
															"border-destructive focus-visible:ring-destructive/50",
													)}
												/>
											)}
										/>
										{pointGrantForm.formState.errors.userId && (
											<p className="text-xs text-destructive mt-1">
												{pointGrantForm.formState.errors.userId.message}
											</p>
										)}
									</label>

									<label className="block">
										<span className={labelClassName}>Points *</span>
										<Input
											type="number"
											min={1}
											{...pointGrantForm.register("points", {
												valueAsNumber: true,
											})}
											className={cn(
												pointGrantForm.formState.errors.points &&
													"border-destructive focus-visible:ring-destructive/50",
											)}
										/>
										{pointGrantForm.formState.errors.points && (
											<p className="text-xs text-destructive mt-1">
												{pointGrantForm.formState.errors.points.message}
											</p>
										)}
									</label>

									<div className="block">
										<span className={labelClassName}>Source *</span>
										<Controller
											control={pointGrantForm.control}
											name="source"
											render={({ field }) => (
												<Select
													value={field.value}
													onValueChange={field.onChange}
												>
													<SelectTrigger
														className={cn(
															pointGrantForm.formState.errors.source &&
																"border-destructive focus-visible:ring-destructive/50",
														)}
													>
														<SelectValue placeholder="Select source" />
													</SelectTrigger>
													<SelectContent>
														{sourceOptions.map((sourceOption) => (
															<SelectItem
																key={sourceOption.value}
																value={sourceOption.value}
															>
																{sourceOption.label}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											)}
										/>
										{pointGrantForm.formState.errors.source && (
											<p className="text-xs text-destructive mt-1">
												{pointGrantForm.formState.errors.source.message}
											</p>
										)}
									</div>
								</div>

								<label className="block">
									<span className={labelClassName}>Source Reference *</span>
									<Input
										type="text"
										{...pointGrantForm.register("sourceRef")}
										placeholder="trivia-2026-02-16-user123"
										className={cn(
											pointGrantForm.formState.errors.sourceRef &&
												"border-destructive focus-visible:ring-destructive/50",
										)}
									/>
									<p className="text-[10px] text-text-tertiary mt-1">
										Use a stable value so retries do not double-award points.
									</p>
									{pointGrantForm.formState.errors.sourceRef && (
										<p className="text-xs text-destructive mt-1">
											{pointGrantForm.formState.errors.sourceRef.message}
										</p>
									)}
								</label>

								<label className="block">
									<span className={labelClassName}>Description</span>
									<Input
										type="text"
										{...pointGrantForm.register("description")}
										placeholder="Trivia winner"
									/>
								</label>

								<Collapsible open={metadataOpen} onOpenChange={setMetadataOpen}>
									<div className="rounded-lg border border-divider bg-bg-tertiary/20 p-3">
										<CollapsibleTrigger asChild>
											<Button
												type="button"
												variant="tertiary"
												size="xs"
												className="gap-2"
											>
												<ChevronDown
													className={cn(
														"h-3.5 w-3.5 transition-transform",
														metadataOpen && "rotate-180",
													)}
												/>
												Optional Metadata
											</Button>
										</CollapsibleTrigger>
										<CollapsibleContent className="mt-3">
											<textarea
												rows={4}
												{...pointGrantForm.register("metadataJson")}
												placeholder='{"gameId":"abc","note":"optional"}'
												className={cn(
													inputClassName,
													"font-mono text-xs",
													pointGrantForm.formState.errors.metadataJson &&
														"border-destructive focus:border-destructive focus:ring-destructive/50",
												)}
											/>
											<p className="text-[10px] text-text-tertiary mt-1">
												Optional JSON object. Leave empty if not needed.
											</p>
											{pointGrantForm.formState.errors.metadataJson && (
												<p className="text-xs text-destructive mt-1">
													{pointGrantForm.formState.errors.metadataJson.message}
												</p>
											)}
										</CollapsibleContent>
									</div>
								</Collapsible>

								{pointGrantsMutation.error instanceof Error && (
									<div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3">
										<p className="text-xs text-destructive">
											{pointGrantsMutation.error.message}
										</p>
									</div>
								)}

								<div className="flex justify-end gap-2 pt-2">
									<Button
										type="button"
										variant="outline"
										onClick={closePointGrantDialog}
										disabled={isGrantSubmitting}
										className="border-divider"
									>
										Cancel
									</Button>
									<Button
										type="submit"
										disabled={isGrantSubmitting}
										className="gap-2"
									>
										{isGrantSubmitting ? (
											<Loader2 className="w-4 h-4 animate-spin" />
										) : (
											<Star className="w-4 h-4" />
										)}
										{isGrantSubmitting ? "Granting..." : "Grant Points"}
									</Button>
								</div>
							</form>
						</DialogContent>
					</Dialog>
					{/* Delete Confirmation Dialog */}
					<AlertDialog
						open={deleteDialogOpen}
						onOpenChange={setDeleteDialogOpen}
					>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>Delete Challenge</AlertDialogTitle>
								<AlertDialogDescription>
									Are you sure you want to delete{" "}
									<span className="font-medium text-text-default">
										"{questToDelete?.name}"
									</span>
									? This action cannot be undone.
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>Cancel</AlertDialogCancel>
								<AlertDialogAction
									onClick={confirmDelete}
									className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
								>
									{deleteMutation.isPending ? (
										<Loader2 className="w-4 h-4 animate-spin mr-2" />
									) : (
										<Trash2 className="w-4 h-4 mr-2" />
									)}
									Delete
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</IsConnectedWrapper>
			</div>
		</main>
	);
}

// --- Quest Group Component ---

function QuestGroup({
	title,
	quests,
	onEdit,
	onDelete,
	onDuplicate,
}: {
	title: string;
	quests: RewardsQuestDetails[];
	onEdit: (quest: RewardsQuestDetails) => void;
	onDelete: (questId: string, questName: string) => void;
	onDuplicate: (quest: RewardsQuestDetails) => void;
}) {
	return (
		<section className="space-y-3">
			<h2 className="text-sm font-medium text-text-tertiary uppercase tracking-wider">
				{title} ({quests.length})
			</h2>
			<div className="space-y-2">
				{quests.map((quest) => (
					<QuestRow
						key={quest.quest.id}
						quest={quest}
						onEdit={() => onEdit(quest)}
						onDelete={() => onDelete(quest.quest.id, quest.quest.name)}
						onDuplicate={() => onDuplicate(quest)}
					/>
				))}
			</div>
		</section>
	);
}

// --- Quest Row Component ---

function QuestRow({
	quest,
	onEdit,
	onDelete,
	onDuplicate,
}: {
	quest: RewardsQuestDetails;
	onEdit: () => void;
	onDelete: () => void;
	onDuplicate: () => void;
}) {
	const { quest: q, steps } = quest;
	const config = statusConfig[q.status];

	return (
		<Button
			type="button"
			onClick={onEdit}
			variant="outline"
			size="md"
			className="w-full py-4 h-auto rounded-xl px-4 justify-start"
		>
			{/* Thumbnail */}
			{q.imageUrl && (
				<div className="w-12 h-12 rounded-lg bg-bg-tertiary overflow-hidden shrink-0 border border-divider mr-4">
					<img src={q.imageUrl} alt="" className="w-full h-full object-cover" />
				</div>
			)}

			{/* Main Info */}
			<div className="flex-1 min-w-0 text-left">
				<div className="flex items-center gap-2 mb-1">
					<h3 className="text-sm font-medium text-text-default truncate">
						{q.name}
					</h3>
					<span
						className={cn(
							"text-[10px] px-1.5 py-0.5 rounded border shrink-0",
							config.className,
						)}
					>
						{config.label}
					</span>
				</div>
				<div className="flex items-center gap-4 text-xs text-text-tertiary">
					<span className="flex items-center gap-1">
						<Trophy className="w-3 h-3" />
						{q.points} pts
					</span>
					<span className="flex items-center gap-1">
						<ListChecks className="w-3 h-3" />
						{steps.length} step{steps.length !== 1 ? "s" : ""}
					</span>
					{(q.startAt || q.endAt) && (
						<span className="flex items-center gap-1">
							<Calendar className="w-3 h-3" />
							{formatDisplayDate(q.startAt)} — {formatDisplayDate(q.endAt)}
						</span>
					)}
				</div>
			</div>

			{/* Actions */}
			<div className="flex items-center gap-1 ">
				<DropdownMenu>
					<DropdownMenuTrigger
						onClick={(e) => e.stopPropagation()}
						className={cn(
							buttonVariants({ variant: "tertiary", size: "icon" }),
						)}
					>
						<MoreVertical className="w-4 h-4" />
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem
							onClick={(e) => {
								e.stopPropagation();
								onDuplicate();
							}}
							className="gap-2"
						>
							<Copy className="w-4 h-4" />
							Duplicate
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							onClick={(e) => {
								e.stopPropagation();
								onDelete();
							}}
							className="gap-2 text-destructive focus:text-destructive focus:bg-destructive/10"
						>
							<Trash2 className="w-4 h-4" />
							Delete
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</Button>
	);
}

// --- Section Header Component ---

function SectionHeader({
	icon: Icon,
	title,
	subtitle,
}: {
	icon: React.ElementType;
	title: string;
	subtitle?: string;
}) {
	return (
		<div className="flex items-center gap-2.5 pb-2 border-b border-white/5">
			<Icon className="w-3.5 h-3.5" />
			<div className="min-w-0">
				<h3 className="text-sm font-medium text-text-default leading-tight">
					{title}
				</h3>
				{subtitle && (
					<p className="text-[10px] text-text-tertiary leading-tight mt-0.5">
						{subtitle}
					</p>
				)}
			</div>
		</div>
	);
}

// --- Live Preview Card (mirrors QuestCard design) ---

function LivePreviewCard({
	name,
	description,
	imageUrl,
	points,
	steps,
}: {
	name: string;
	description: string | null;
	imageUrl: string | null | undefined;
	points: number;
	steps: { name: string }[];
}) {
	return (
		<div className="rounded-[18px] p-2.5 bg-[#121214] border border-[#27272a] shadow-xl max-w-[220px] mx-auto">
			<div className="relative flex flex-col h-full w-full rounded-[12px] overflow-hidden bg-[#18181b] border border-white/5">
				{/* Noise Texture */}
				<div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] pointer-events-none z-0" />

				{/* --- Header Bar --- */}
				<div className="relative z-10 flex items-center justify-between gap-2 min-h-[28px] px-2.5 py-1.5 bg-[#202023] border-b border-white/5">
					<div className="flex items-start gap-1.5 min-w-0 flex-1">
						<span className="text-[8px] font-medium text-text-default line-clamp-2 ml-0.5 leading-snug">
							{name || "Challenge Name"}
						</span>
					</div>
					{/* Points Badge */}
					<div className="relative flex items-center gap-0.5 h-5 px-1.5 rounded-md border border-brand/20 shrink-0">
						<Star className="w-2.5 h-2.5 fill-brand text-brand" />
						<span className="text-[8px] font-extrabold text-brand tabular-nums">
							{points || 0}
						</span>
						<span className="text-[5px] font-bold text-text-secondary uppercase ml-px">
							pts
						</span>
					</div>
				</div>

				{/* --- Card Art (1:1 Aspect Ratio) --- */}
				<div className="relative w-full aspect-square bg-black/40 overflow-hidden border-b border-white/5">
					{imageUrl ? (
						<img src={imageUrl} alt="" className="w-full h-full object-cover" />
					) : (
						<div className="w-full h-full flex flex-col items-center justify-center bg-linear-to-br from-[#202023] to-[#18181b]">
							<LucideImage className="w-6 h-6 text-text-tertiary/20 mb-1" />
							<span className="text-[5px] text-text-tertiary/30 uppercase tracking-widest font-bold">
								Shinkai Quest
							</span>
						</div>
					)}
				</div>

				{/* --- Card Body --- */}
				<div className="relative flex-1 flex flex-col p-2.5 z-10">
					{/* Type Line */}
					<div className="flex items-center gap-1.5 mb-1.5 pb-1">
						<span className="text-[6px] uppercase tracking-widest font-bold text-text-tertiary">
							Challenge
						</span>
						<div className="h-px flex-1 bg-white/5" />
					</div>

					{/* Description */}
					<div className="flex gap-1.5 mb-2 pb-1">
						<div className="mt-px shrink-0 opacity-50">
							<FileText className="w-2.5 h-2.5 text-text-tertiary" />
						</div>
						<p className="text-[7px] text-text-secondary leading-relaxed line-clamp-2">
							{description || "Challenge description will appear here..."}
						</p>
					</div>

					{/* CTA */}
					<div className="mt-auto pt-1.5">
						<div className="w-full h-5 rounded flex items-center justify-center bg-[#202023] border border-white/10 hover:border-brand/30 transition-all">
							<span className="text-[7px] uppercase tracking-wide font-bold text-text-tertiary">
								Start Challenge
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

// --- Event Filters Section Component ---

function EventFiltersSection({
	eventName,
	index,
	form,
}: {
	eventName: string;
	index: number;
	form: ReturnType<typeof useForm<QuestFormData>>;
}) {
	const [isExpanded, setIsExpanded] = useState(false);
	const filterConfig = EVENT_FILTER_CONFIG[eventName];

	// Get current filter values
	const currentFilters = form.watch(`steps.${index}.eventPropsFilter`) ?? {};
	const hasActiveFilters = Object.values(currentFilters).some(
		(v) => v !== "" && v !== undefined,
	);

	// If no filter config for this event, don't show the section
	if (!filterConfig || filterConfig.length === 0) {
		return null;
	}

	return (
		<div className="mt-3 border border-divider rounded-lg overflow-hidden bg-bg-secondary/30">
			<button
				type="button"
				onClick={() => setIsExpanded(!isExpanded)}
				className="w-full px-3 py-2.5 flex items-center justify-between text-left hover:bg-bg-tertiary/30 transition-colors"
			>
				<div className="flex items-center gap-2">
					<Filter className="w-3.5 h-3.5 text-text-tertiary" />
					<span className="text-xs font-medium text-text-secondary">
						Event Filters
					</span>
					{hasActiveFilters && (
						<span className="text-[10px] px-1.5 py-0.5 rounded bg-brand/10 text-brand border border-brand/20">
							Active
						</span>
					)}
				</div>
				<ChevronDown
					className={cn(
						"w-4 h-4 text-text-tertiary transition-transform",
						isExpanded && "rotate-180",
					)}
				/>
			</button>

			{isExpanded && (
				<div className="px-3 pb-3 pt-1 space-y-3 border-t border-divider">
					<p className="text-[10px] text-text-tertiary">
						Filter events by specific properties. Leave empty to match all
						events of this type.
					</p>

					{filterConfig.map((field) => (
						<div key={field.key} className="block">
							{field.type === "checkbox" ? (
								<div className="flex items-center justify-between">
									<span className="text-xs text-text-secondary">
										{field.label}
									</span>
									<Controller
										control={form.control}
										name={
											`steps.${index}.eventPropsFilter.${field.key}` as const
										}
										render={({ field: controllerField }) => (
											<Switch
												checked={controllerField.value === "true"}
												onCheckedChange={(checked) => {
													controllerField.onChange(checked ? "true" : "");
												}}
											/>
										)}
									/>
								</div>
							) : (
								<>
									<span className="text-xs text-text-secondary mb-1 block">
										{field.label}
									</span>
									<Controller
										control={form.control}
										name={
											`steps.${index}.eventPropsFilter.${field.key}` as const
										}
										render={({ field: controllerField }) => (
											<input
												type="text"
												value={controllerField.value ?? ""}
												onChange={(e) =>
													controllerField.onChange(e.target.value)
												}
												placeholder={field.placeholder}
												className={cn(inputClassName, "text-xs py-2")}
											/>
										)}
									/>
								</>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
}
